import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Copy, Check, Server, Terminal, DollarSign, Wifi, ArrowRight, HardDrive, Cpu } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function DocsMacMiniSetup() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const brewInstall = `# Install Homebrew (if not already installed)
/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"

# Install required services
brew install mongodb-community redis ollama`;

  const cloneSetup = `# Clone the OpenClaw repository
git clone https://github.com/commerce-index/openclaw.git
cd openclaw

# Run setup with macmini mode
./setup.sh macmini`;

  const envConfig = `# .env configuration for Mac Mini
MONGODB_URI=mongodb://localhost:27017/openclaw
REDIS_URL=redis://localhost:6379
CI_API_URL=https://api.commerceindex.ai
OLLAMA_URL=http://localhost:11434
TELEGRAM_BOT_TOKEN=your_telegram_bot_token
WHATSAPP_PHONE_ID=your_whatsapp_phone_id
WHATSAPP_TOKEN=your_whatsapp_token
ANTHROPIC_API_KEY=your_anthropic_api_key  # fallback LLM
NODE_ENV=production
PORT=3000
LOG_LEVEL=info`;

  const dockerCompose = `# Start all services
docker-compose up -d

# Verify all containers are running
docker-compose ps

# Expected output:
# NAME              STATUS    PORTS
# openclaw-api      running   0.0.0.0:3000->3000/tcp
# openclaw-worker   running
# mongodb           running   0.0.0.0:27017->27017/tcp
# redis             running   0.0.0.0:6379->6379/tcp`;

  const cloudflareTunnel = `# Install cloudflared
brew install cloudflare/cloudflare/cloudflared

# Authenticate with Cloudflare
cloudflared tunnel login

# Create the tunnel
cloudflared tunnel create ci-admin

# Configure ingress rules (~/.cloudflared/config.yml)
tunnel: <your-tunnel-id>
credentials-file: /Users/<you>/.cloudflared/<tunnel-id>.json

ingress:
  - hostname: api.yourdomain.com
    service: http://localhost:3000
  - hostname: admin.yourdomain.com
    service: http://localhost:3001
  - service: http_status:404

# Route DNS
cloudflared tunnel route dns ci-admin api.yourdomain.com
cloudflared tunnel route dns ci-admin admin.yourdomain.com

# Start the tunnel
cloudflared tunnel run ci-admin`;

  const launchdPlist = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN"
  "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
  <key>Label</key>
  <string>com.commerceindex.openclaw</string>
  <key>ProgramArguments</key>
  <array>
    <string>/usr/local/bin/docker-compose</string>
    <string>-f</string>
    <string>/Users/admin/openclaw/docker-compose.yml</string>
    <string>up</string>
    <string>-d</string>
  </array>
  <key>RunAtLoad</key>
  <true/>
  <key>KeepAlive</key>
  <false/>
  <key>WorkingDirectory</key>
  <string>/Users/admin/openclaw</string>
  <key>StandardOutPath</key>
  <string>/Users/admin/openclaw/logs/launchd.log</string>
  <key>StandardErrorPath</key>
  <string>/Users/admin/openclaw/logs/launchd-error.log</string>
</dict>
</plist>

<!-- Save as ~/Library/LaunchAgents/com.commerceindex.openclaw.plist -->
<!-- Then load it: -->
<!-- launchctl load ~/Library/LaunchAgents/com.commerceindex.openclaw.plist -->`;

  const ollamaPull = `# Pull recommended models
ollama pull mistral:7b        # Intent classification (~4GB)
ollama pull llama3:8b          # Text generation (~4.7GB)

# Verify models are available
ollama list

# Expected output:
# NAME            SIZE     MODIFIED
# mistral:7b      4.1 GB   Just now
# llama3:8b       4.7 GB   Just now`;

  const healthCheck = `# Test the API health endpoint
curl http://localhost:3000/health

# Expected response:
# {
#   "status": "ok",
#   "version": "1.0.0",
#   "services": {
#     "mongodb": "connected",
#     "redis": "connected",
#     "ollama": "connected"
#   },
#   "uptime": 42
# }`;

  const costs = [
    { item: 'Mac Mini M2 (8GB)', type: 'One-time', cost: '$599' },
    { item: 'Mac Mini M4 (16GB)', type: 'One-time', cost: '$799' },
    { item: 'Electricity (~15W avg)', type: 'Monthly', cost: '~$3-5' },
    { item: 'Cloudflare Tunnel', type: 'Monthly', cost: 'Free' },
    { item: 'Domain name', type: 'Monthly', cost: '~$1-2' },
    { item: 'Internet (existing)', type: 'Monthly', cost: '$0' },
    { item: 'Claude API (fallback)', type: 'Monthly', cost: '~$5-15' },
    { item: 'MongoDB Atlas (optional)', type: 'Monthly', cost: '~$15-30' },
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Infrastructure
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Mac Mini Setup
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Run Commerce Index on a Mac Mini for a self-hosted, cost-effective deployment.
          Complete guide from hardware to production-ready setup.
        </p>
      </div>

      {/* Hardware Requirements */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Cpu className="w-6 h-6 text-[#3EF1C7]" />
          Hardware Requirements
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-[#E2E8F0]">Minimum: Mac Mini M2</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-[#94A3B8] text-sm">
                <li>8GB Unified Memory</li>
                <li>256GB SSD</li>
                <li>Runs Mistral 7B for intent classification</li>
                <li>Handles up to ~50 merchants</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <CardTitle className="text-[#E2E8F0]">Recommended: Mac Mini M4</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-[#94A3B8] text-sm">
                <li>16GB+ Unified Memory</li>
                <li>512GB SSD</li>
                <li>Runs Llama 3 8B and Mistral 7B simultaneously</li>
                <li>Handles up to ~200 merchants</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Step 1: Docker */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Step 1: Install Docker Desktop
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Download and install <a href="https://www.docker.com/products/docker-desktop/" className="text-[#3EF1C7] hover:underline" target="_blank" rel="noopener noreferrer">Docker Desktop for Mac</a>.
          Ensure it is running before proceeding.
        </p>
      </section>

      {/* Step 2: Homebrew */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Step 2: Install Dependencies via Homebrew
        </h2>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(brewInstall, 'brew')}
          >
            {copiedCode === 'brew' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{brewInstall}</code>
          </pre>
        </div>
      </section>

      {/* Step 3: Clone */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Step 3: Clone Repository & Run Setup
        </h2>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(cloneSetup, 'clone')}
          >
            {copiedCode === 'clone' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{cloneSetup}</code>
          </pre>
        </div>
      </section>

      {/* Step 4: .env */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Step 5: Configure Environment
        </h2>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(envConfig, 'env')}
          >
            {copiedCode === 'env' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{envConfig}</code>
          </pre>
        </div>
      </section>

      {/* Step 5: Docker Compose */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Step 6: Start Services
        </h2>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(dockerCompose, 'docker')}
          >
            {copiedCode === 'docker' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{dockerCompose}</code>
          </pre>
        </div>
      </section>

      {/* Step 6: Cloudflare Tunnel */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <Wifi className="w-6 h-6 text-[#3EF1C7]" />
          Step 7: Set Up Cloudflare Tunnel
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Cloudflare Tunnel provides a secure way to expose your local services to the internet
          without opening ports on your router.
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(cloudflareTunnel, 'cloudflare')}
          >
            {copiedCode === 'cloudflare' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{cloudflareTunnel}</code>
          </pre>
        </div>
      </section>

      {/* Step 7: launchd */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <HardDrive className="w-6 h-6 text-[#3EF1C7]" />
          Step 8: Auto-Start on Boot (launchd)
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Configure macOS launchd to automatically start Commerce Index services when the Mac Mini boots up.
        </p>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(launchdPlist, 'launchd')}
          >
            {copiedCode === 'launchd' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{launchdPlist}</code>
          </pre>
        </div>
      </section>

      {/* Step 8: Ollama Models */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Step 9: Pull Ollama Models
        </h2>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(ollamaPull, 'ollama')}
          >
            {copiedCode === 'ollama' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{ollamaPull}</code>
          </pre>
        </div>
      </section>

      {/* Step 9: Health Check */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4">
          Step 10: Verify Installation
        </h2>
        <div className="relative mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 text-[#94A3B8] hover:text-white"
            onClick={() => copyToClipboard(healthCheck, 'health')}
          >
            {copiedCode === 'health' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]">
            <code>{healthCheck}</code>
          </pre>
        </div>
      </section>

      {/* Cost Analysis */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-4 flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-[#3EF1C7]" />
          Cost Analysis
        </h2>
        <p className="text-[#94A3B8] mb-4">
          Mac Mini M2 starts at $599 one-time, with ongoing costs of approximately $25-55/month
          depending on your configuration.
        </p>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Item</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Type</th>
                  <th className="text-left p-3 text-[#94A3B8] font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {costs.map((row, i) => (
                  <tr key={i} className="border-b border-[#22304A] last:border-0">
                    <td className="p-3 text-[#E2E8F0]">{row.item}</td>
                    <td className="p-3">
                      <Badge className={row.type === 'One-time'
                        ? 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30 text-xs'
                        : 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30 text-xs'
                      }>
                        {row.type}
                      </Badge>
                    </td>
                    <td className="p-3 text-[#3EF1C7] font-medium">{row.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* What's Next */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">What's Next?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/docs/local-llm">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Local LLM Setup <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Configure Ollama models and Claude API fallback.</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/webhook-setup">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer">
              <CardContent className="pt-6">
                <h3 className="text-[#E2E8F0] font-semibold mb-2 flex items-center gap-2">
                  Webhook Setup <ArrowRight className="w-4 h-4 text-[#3EF1C7]" />
                </h3>
                <p className="text-[#94A3B8] text-sm">Configure webhooks to receive real-time events.</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
